import styled from 'styled-components';
import { COLOR } from '@src/styles/color';

// The two slots flanking the play button (design: 176px for the create-audio
// one, Figma 2541:1648). Both are fixed AND equal, so the play button lands on
// the group's centre — with only one of them fixed, the empty part of that slot
// pushed everything visible to one side — and so no text inside them can change
// the row's layout.
const SIDE_SLOT_WIDTH = 176;

export const StyledToolbar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 8px 12px;
  /* Dropped when something else already draws the dock's top edge — the
     collapsed progress slider sits flush above the row and is that edge. */
  border-top: ${({ hideTopBorder }) =>
    hideTopBorder ? 'none' : `0.5px solid ${COLOR.indigo[16]}`};
  background: ${COLOR.white};
`;

export const StyledSide = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: ${({ end }) => (end ? 'flex-end' : 'flex-start')};
`;

// Transport group: [create-audio slot] [play] [clock], left to right. Its width
// is the sum of fixed parts, so it never changes — it sits between two flex:1
// sides, and an auto width that tracked its content would re-centre the group,
// and shift the buttons, on every clock tick.
export const StyledCenter = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
`;

// The create-audio button and the "creating audio (x%)" line share this slot,
// right-aligned against the play button, so swapping one for the other doesn't
// move anything either.
export const StyledStatusSlot = styled.div`
  box-sizing: border-box;
  flex: 0 0 ${SIDE_SLOT_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const StyledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 480px;
  background: transparent;
  color: ${COLOR.darkBlue};
  cursor: pointer;

  &:hover {
    background: ${COLOR.indigo[8]};
  }

  svg {
    font-size: 24px;
  }
`;

// Tooltip anchor for the generate button, and the reason hiding it doesn't move
// the toolbar: `visibility: hidden` keeps the slot's box (and drops hover/click),
// where not rendering it would pull the play button and the clock left.
export const StyledGenerateSlot = styled.span`
  display: inline-flex;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
`;

export const StyledGenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 4px 8px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background: ${COLOR.charcoal};
  color: ${COLOR.darkBlue};
  font-size: 14px;
  cursor: pointer;

  svg {
    font-size: 20px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const StyledConvertingText = styled.span`
  display: inline-flex;
  align-items: center;
  height: 32px;
  font-size: 14px;
  color: ${COLOR.label2[64]};
  white-space: nowrap;
`;

export const StyledPlayButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 480px;
  background: ${COLOR.yellow.default};
  color: ${COLOR.darkBlue};
  cursor: pointer;

  svg {
    font-size: 24px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const StyledTime = styled.div`
  /* Mirrors the create-audio slot's width so the play button sits dead centre,
     and so the clock grows inside its own slot instead of widening the row. */
  box-sizing: border-box;
  flex: 0 0 ${SIDE_SLOT_WIDTH}px;
  padding: 10px;
  font-size: 14px;
  color: ${COLOR.label2[64]};
  white-space: nowrap;
  /* Digits share one advance width, so the "/ total" after the live value stays
     put as the numbers change — in a proportional font "1" is narrower than "0".
     The fixed transport width keeps this row's neighbours still; this keeps the
     row's own contents still. */
  font-variant-numeric: tabular-nums;
`;

/* Zoom slider — a light-gray track with a navy fill and NO visible thumb
 * (per design). The native input sits on top, transparent, to stay
 * clickable/draggable while the fill below shows the current level. */
export const StyledZoomSlider = styled.div`
  position: relative;
  width: 100px;
  height: 4px;
  border-radius: 999px;
  background: ${COLOR.indigo[8]};
`;

export const StyledZoomFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: ${COLOR.darkBlue};
  pointer-events: none;
`;

export const StyledZoomInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;

  /* Invisible thumb — no knob in the design, but keep it draggable. */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  &::-moz-range-track {
    background: transparent;
  }
`;

export const StyledFitButton = styled.button`
  height: 32px;
  padding: 4px 16px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background: transparent;
  color: ${COLOR.darkGray};
  font-size: 14px;
  cursor: pointer;
`;
